import { useMemo } from 'react';
import {
	SearchableSelect,
	type SearchableSelectOption,
} from '@/components/ui/searchable-select';
import {
	buildLeadSourceOptions,
	toLeadSourceSearchableOptions,
} from '@/lib/leadSources';
import { useDoctorLeadSourceOptions } from '@/hooks/useDoctorLeadSourceOptions';
import { mergeDoctorLeadSourceOptions } from '@/lib/doctorLeadSources';

export type LeadSourceSearchableSelectProps = {
	value: string;
	onValueChange: (value: string) => void;
	/** Hospital-specific CRM sources merged into the canonical list. */
	extraApiSources?: string[];
	/** Merge active Dr {Name} Lead sources for this hospital. */
	includeDoctorLeadSources?: boolean;
	hospitalId?: string;
	/** Full override (e.g. CRM lead detail with dynamic API list). */
	options?: SearchableSelectOption[];
	placeholder?: string;
	searchPlaceholder?: string;
	disabled?: boolean;
	id?: string;
	className?: string;
	triggerClassName?: string;
	contentClassName?: string;
};

export function LeadSourceSearchableSelect({
	value,
	onValueChange,
	extraApiSources,
	includeDoctorLeadSources = true,
	hospitalId,
	options: optionsOverride,
	placeholder = 'Select source…',
	searchPlaceholder = 'Search source…',
	disabled,
	id,
	className,
	triggerClassName,
	contentClassName,
}: LeadSourceSearchableSelectProps) {
	const { doctorSources } = useDoctorLeadSourceOptions(
		includeDoctorLeadSources ? hospitalId : undefined
	);

	const options = useMemo(() => {
		if (optionsOverride?.length) return optionsOverride;
		let src = buildLeadSourceOptions(extraApiSources);
		if (includeDoctorLeadSources) {
			src = mergeDoctorLeadSourceOptions(src, doctorSources);
		}
		return toLeadSourceSearchableOptions(src);
	}, [optionsOverride, extraApiSources, includeDoctorLeadSources, doctorSources]);

	return (
		<SearchableSelect
			id={id}
			value={value}
			onValueChange={onValueChange}
			options={options}
			placeholder={placeholder}
			searchPlaceholder={searchPlaceholder}
			disabled={disabled}
			className={className}
			triggerClassName={triggerClassName}
			contentClassName={contentClassName}
			emptyMessage="No matching source."
		/>
	);
}

export type { SearchableSelectOption };
